"use client";

import { SectionIntro } from "../SectionIntro/SectionIntro";
import { VideoPlaylist, type VideoPlaylistItem } from "./VideoPlaylist";
import classNames from "classnames/bind";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { ReactNode } from "react";
import styles from "./StepCarousel.module.css";

const cx = classNames.bind(styles);

interface StepCarouselCard {
  label: string;
  copy?: ReactNode;
  highlighted?: boolean | null;
}

interface StepCarouselProps {
  kicker?: string;
  headline: string;
  copy?: ReactNode;
  cards: StepCarouselCard[];
  videos?: VideoPlaylistItem[];
}

export const StepCarousel = ({
  kicker,
  headline,
  copy,
  cards,
  videos,
}: StepCarouselProps) => {
  const [emblaRef] = useEmblaCarousel(
    {
      align: "start",
      loop: false,
      skipSnaps: false,
      slidesToScroll: 1,
      containScroll: "trimSnaps",
      watchDrag: true,
      dragFree: true,
    },
    [WheelGesturesPlugin({ forceWheelAxis: "x" })],
  );

  if (!cards?.length) {
    return null;
  }

  return (
    <section className={cx("section", "step-carousel")}>
      <div className={cx("container")}>
        <div className={cx("inner")}>
          <div className={cx("intro")}>
            <SectionIntro variant="split" kicker={kicker} headline={headline}>
              {copy}
            </SectionIntro>
          </div>
          {videos?.length ? (
            <VideoPlaylist className={cx("media")} videos={videos} />
          ) : null}
          <div className={cx("embla-shell")}>
            <div className={cx("embla")}>
              <div className={cx("embla-viewport")} ref={emblaRef}>
                <div className={cx("embla-container")}>
                  {cards.map((card, index) => (
                    <div className={cx("embla-slide")} key={`step-${index}`}>
                      <article
                        className={cx("card", {
                          highlighted: card.highlighted,
                        })}
                      >
                        <div className={cx("step-number")}>
                          {(index + 1).toString().padStart(2, "0")}
                        </div>
                        <h3 className={cx("card-title")}>{card.label}</h3>
                        {card.copy ? (
                          <div className={cx("card-copy")}>{card.copy}</div>
                        ) : null}
                      </article>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
